import ModalContainer from 'components/modal/modal-container'

import ModalActions from 'components/modal/modal-actions'
import { Alert, Box, Button, Typography } from '@mui/material'

import { closeModal } from 'actions/app'

import { deleteLegend } from 'api/legend'
import { useTranslation } from 'react-i18next'

import type { AppState } from 'state/index'
import type { ThunkDispatch } from 'redux-thunk'
import type { UnknownAction } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'
import { TEST_IDS } from 'test-ids'
import {
  deleteLegendsInOrder,
  isGlobalDemLegend,
  type DeletableLegend,
} from './selection'

export type DeleteLegendProps = {
  selectedLegend?: DeletableLegend
  selectedLegends?: DeletableLegend[]
  onDemLegendDeleted?: () => void
  onDeleted?: (deletedIds: number[]) => void
}

export default function DeleteLegend(props: DeleteLegendProps) {
  const { t } = useTranslation()
  const dispatch =
    useDispatch<ThunkDispatch<AppState, unknown, UnknownAction>>()
  const { selectedLegend, selectedLegends, onDemLegendDeleted, onDeleted } =
    props
  const crops = useSelector(
    (state: AppState) => state.crop.cropConstantsCollection,
  )

  // One legend or a grid selection; the single-legend callers predate
  // multi-select and still pass one.
  const initialLegends = useMemo(
    () =>
      selectedLegends?.length
        ? selectedLegends
        : selectedLegend
          ? [selectedLegend]
          : [],
    [selectedLegend, selectedLegends],
  )

  // What a Delete press would still remove. A partial failure leaves only the
  // legends that failed, so pressing Delete again retries those rather than
  // re-deleting the ones already gone.
  const [pending, setPending] = useState<DeletableLegend[]>(initialLegends)
  const [deleting, setDeleting] = useState(false)
  const [failed, setFailed] = useState<string[]>([])

  const labelFor = useCallback(
    (legend: DeletableLegend) => {
      // Name the season too: with one legend per crop year, the crop and
      // parameter alone no longer identify which row is about to go.
      const season = legend.cropYear ? ` - ${legend.cropYear}` : ''
      const crop = crops?.find((c) => c.id === legend.cropId)
      return crop
        ? `${t(crop.name)} - ${t(legend.parameter)}${season}`
        : `${t(legend.parameter)}${season}`
    },
    [crops, t],
  )

  const closePopup = () => dispatch(closeModal())

  const deleteItem = useCallback(async () => {
    setDeleting(true)
    setFailed([])

    const { deletedIds, failures } = await deleteLegendsInOrder(
      pending,
      (legend) =>
        dispatch(
          // The dialog reports its own failures. The global alert modal would
          // take this dialog's place, hiding which legends still remain.
          deleteLegend(legend.id, undefined, undefined, {
            suppressErrorModal: true,
          }),
        ),
    )

    setDeleting(false)
    setPending(failures)
    if (deletedIds.length && onDeleted) onDeleted(deletedIds)
    if (
      onDemLegendDeleted &&
      pending.some(
        (legend) => isGlobalDemLegend(legend) && deletedIds.includes(legend.id),
      )
    ) {
      onDemLegendDeleted()
    }

    if (failures.length === 0) {
      dispatch(closeModal())
      return
    }
    setFailed(failures.map(labelFor))
  }, [dispatch, labelFor, onDeleted, onDemLegendDeleted, pending])

  const warning =
    pending.length === 1
      ? t('delete_item_warning', { name: labelFor(pending[0]) })
      : t('delete_multiple_item_warning', {
          count: pending.length,
          item: t('crop_legends').toLocaleLowerCase(),
        })

  return (
    <ModalContainer>
      <Box data-testid={TEST_IDS.deleteCropLegendDialog}>
        <div>{warning}</div>
        {pending.length > 1 ? (
          <Box
            sx={{ marginTop: '0.75rem', maxHeight: '30vh', overflowY: 'auto' }}
          >
            {pending.map((legend) => (
              <Typography key={legend.id} variant="body2">
                {labelFor(legend)}
              </Typography>
            ))}
          </Box>
        ) : null}
        {failed.length ? (
          <Alert
            severity="error"
            sx={{ marginTop: '1rem' }}
            data-testid={TEST_IDS.deleteCropLegendError}
          >
            {t('delete_failed_items', { items: failed.join(', ') })}
          </Alert>
        ) : null}
      </Box>
      <ModalActions>
        <Button variant="contained" color="secondary" onClick={closePopup}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={deleteItem}
          disabled={deleting || pending.length === 0}
          data-testid={TEST_IDS.deleteCropLegendConfirmButton}
        >
          {t(deleting ? 'deleting' : 'delete')}
        </Button>
      </ModalActions>
    </ModalContainer>
  )
}
